import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    CircleSpinner,
    Colours,
    MessagePropType,
    FilterGroup,
    toDate,
    DefaultIntlFormatValues,
    FilterPropType,
    LegacyCityPropType,
    LocationSelector,
    Question,
} from '@ratehub/base-ui';

import COMMON_MESSAGES from '../definitions/CommonMessages';
import ProductTableColumnPropType from '../definitions/ProductTableColumnPropType';
import ProductTableRowPropType from '../definitions/ProductTableRowPropType';
import ResponsiveTable from './ResponsiveTable';
import AdvertisingDisclosure from './AdvertisingDisclosure';


function ProductTable({
    title,
    hideTimestamp = false,
    lastUpdatedDate,
    showAdDisclosure = false,

    locationSelectorLabel,

    rows,
    columns,
    maxRowsCollapsed,
    maxRowsExpanded,
    isAlwaysExpanded = false,

    initialProvinceCode,
    initialCity,
    filters,
    showSpinner,

    onChangeFilters,
    onChangeLocation,

    tableCtaMessage,
    tableCtaHref,

    noRowDataMessage,

    ...otherProps
}) {
    const intl = useIntl();
    const showDot = !/[.;?!:]/.test(title?.slice(-1)); // show a trailing dot if title does not end with one of these

    return (
        <Container {...otherProps}>
            <If condition={showSpinner}>
                <CircleSpinner />
            </If>

            <header className="header">
                <div className="flex-container rh-mb-1_5">
                    <If condition={title}>
                        <h2
                            className={classNames(
                                'rh-title-2xl',
                                'rh-mt-0',
                                'rh-mb-0_5',
                                {
                                    showDot: showDot,
                                },
                            )}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: title,
                            }}
                        />
                    </If>

                    <If condition={!hideTimestamp && lastUpdatedDate}>
                        <p className="rh-text-s rh-my-0">
                            {intl.formatMessage(COMMON_MESSAGES.ratesUpdated, {
                                timestamp: toDate(lastUpdatedDate),
                                ...DefaultIntlFormatValues,
                            })}
                        </p>
                    </If>
                </div>

                <If condition={onChangeLocation}>
                    <div className="flex-container location-selector-wrapper rh-mb-1_5">
                        <Question
                            id="product-table-location-selector"
                            field={{
                                name: 'product-table-location-selector',
                                value: initialCity,
                            }}
                            type={LocationSelector}
                            labelMessage={locationSelectorLabel}
                            initialProvince={initialProvinceCode}
                            onChange={onChangeLocation}
                        />
                    </div>
                </If>
            </header>

            <If condition={showAdDisclosure}>
                <AdvertisingDisclosure
                    className="rh-display-block rh-mb-1_5"
                    size="extra-small"
                />
            </If>

            <If condition={onChangeFilters && filters}>
                <div className="filterGroupContainer rh-py-1">
                    <FilterGroup
                        filters={filters}
                        onChange={onChangeFilters}
                        isDisabled={showSpinner}
                    />
                </div>
            </If>

            <ResponsiveTable
                footerCtaMessage={tableCtaMessage}
                footerCtaHref={tableCtaHref}
                columns={columns}
                rows={rows}
                maxRowsCollapsed={maxRowsCollapsed}
                maxRowsExpanded={maxRowsExpanded}
                noRowDataMessage={noRowDataMessage}
                isAlwaysExpanded={isAlwaysExpanded}
            />
        </Container>
    );
}

ProductTable.propTypes = {
    title: PropTypes.string,
    hideTimestamp: PropTypes.bool,
    lastUpdatedDate: PropTypes.string,
    showAdDisclosure: PropTypes.bool,

    locationSelectorLabel: PropTypes.string,

    rows: ProductTableRowPropType.isRequired,
    columns: ProductTableColumnPropType.isRequired,
    tableCtaMessage: PropTypes.string,
    tableCtaHref: PropTypes.string,
    maxRowsCollapsed: PropTypes.number,
    maxRowsExpanded: PropTypes.number,
    isAlwaysExpanded: PropTypes.bool,

    initialProvinceCode: PropTypes.string.isRequired,
    initialCity: LegacyCityPropType,

    showSpinner: PropTypes.bool.isRequired,
    filters: PropTypes.oneOfType([
        PropTypes.arrayOf(FilterPropType),
        PropTypes.object, // mobx observable array
    ]),

    onChangeFilters: PropTypes.func,
    onChangeLocation: PropTypes.func,

    noRowDataMessage: MessagePropType,
};

const Container = styled.div`
    position: relative;

    > .header {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;

        > .flex-container {
            display: flex;
            flex-direction: column;

            &.location-selector-wrapper {
                flex-basis: 285px;
            }
        }
    }

    > .filterGroupContainer {
        border: 1px solid ${Colours.STONE};
        border-bottom-width: 0;
        background-color: ${Colours.STONE_LIGHTEST};
    }
`;

export default ProductTable;
